import React, { useState, useMemo, useEffect } from 'react';

const PAGE_SIZE = 10;

export default function EnrollmentsView({ users = [] }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  /* Reset to page 1 when search changes */
  useEffect(() => {
    setPage(1);
  }, [search]);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.includes(search)
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedUsers = filteredUsers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const from = filteredUsers.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const to = Math.min(safePage * PAGE_SIZE, filteredUsers.length);

  return (
    <div className="CourseAdmin-enrollments-container">
      <div className="CourseAdmin-table-header-info">
        <h3>Registered Student Profiles &amp; Course Subscriptions</h3>
        <p>
          {filteredUsers.length} student
          {filteredUsers.length === 1 ? '' : 's'} found
          {search && ' matching your search'}
        </p>
      </div>

      <div className="CourseAdmin-toolbar">
        <input
          className="CourseAdmin-form-input CourseAdmin-toolbar-search"
          placeholder="🔍 Search students by name, email or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="CourseAdmin-empty-state">
          <span className="icon">👥</span>
          <span>
            {users.length === 0
              ? 'No registered students found in database.'
              : 'No students match your search.'}
          </span>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="CourseAdmin-table-wrapper">
            <table className="CourseAdmin-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Email Address</th>
                  <th>Phone</th>
                  <th>Joined Date</th>
                  <th>Enrolled Courses</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((u) => (
                  <tr key={u._id}>
                    <td data-label="Name">
                      <strong>{u.name}</strong>
                    </td>
                    <td data-label="Email" className="cell-truncate">
                      {u.email}
                    </td>
                    <td data-label="Phone">{u.phone || 'N/A'}</td>
                    <td data-label="Joined">
                      {u.createdAt
                        ? new Date(u.createdAt).toLocaleDateString()
                        : 'N/A'}
                    </td>
                    <td data-label="Courses">
                      {u.enrolledCourses?.length > 0 ? (
                        <div className="CourseAdmin-user-course-tags">
                          {u.enrolledCourses.map((c) => (
                            <span
                              key={c._id}
                              className="CourseAdmin-user-course-badge"
                            >
                              📖 {c.title || 'Course'} (
                              {c.duration || 'N/A'})
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="CourseAdmin-text-muted">
                          No Enrolled Courses
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="CourseAdmin-mobile-cards">
            {paginatedUsers.map((u) => (
              <div key={u._id} className="CourseAdmin-mobile-card">
                <div className="CourseAdmin-mobile-card-header">
                  <strong>{u.name}</strong>
                  <span className="CourseAdmin-mobile-date">
                    {u.createdAt
                      ? new Date(u.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </span>
                </div>
                <div className="CourseAdmin-mobile-row">
                  <span className="CourseAdmin-mobile-label">Email</span>
                  <span className="CourseAdmin-mobile-value">{u.email}</span>
                </div>
                <div className="CourseAdmin-mobile-row">
                  <span className="CourseAdmin-mobile-label">Phone</span>
                  <span className="CourseAdmin-mobile-value">
                    {u.phone || 'N/A'}
                  </span>
                </div>
                <div className="CourseAdmin-mobile-row column">
                  <span className="CourseAdmin-mobile-label">Courses</span>
                  {u.enrolledCourses?.length > 0 ? (
                    <div className="CourseAdmin-user-course-tags">
                      {u.enrolledCourses.map((c) => (
                        <span
                          key={c._id}
                          className="CourseAdmin-user-course-badge"
                        >
                          📖 {c.title || 'Course'} ({c.duration || 'N/A'})
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="CourseAdmin-text-muted">
                      No Enrolled Courses
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="CourseAdmin-pagination">
              <span className="CourseAdmin-page-info">
                Showing <strong>{from}</strong>–<strong>{to}</strong> of{' '}
                <strong>{filteredUsers.length}</strong>
              </span>
              <div className="CourseAdmin-page-controls">
                <button
                  type="button"
                  className="CourseAdmin-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  aria-label="Previous page"
                >
                  ←
                </button>

                {buildPageNumbers(safePage, totalPages).map((n, i) =>
                  n === '…' ? (
                    <span
                      key={`ellipsis-${i}`}
                      style={{
                        padding: '0 0.4rem',
                        color: 'var(--text-muted, #94a3b8)',
                      }}
                    >
                      …
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      className={`CourseAdmin-page-btn ${
                        n === safePage ? 'active' : ''
                      }`}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  )
                )}

                <button
                  type="button"
                  className="CourseAdmin-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  aria-label="Next page"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   PAGE NUMBER BUILDER (with ellipsis)
   ========================================================================== */

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);

  pages.push(1);
  if (left > 2) pages.push('…');
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push('…');
  pages.push(total);

  return pages;
}